import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  CheckCircle, Clock, AlertCircle, ArrowLeft, Copy, Loader2, QrCode, ExternalLink,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PORTAL_PAYNOW_PAGE = 'https://portal.sccfootball.com/academy/paynow-payment';

export default function EventSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const registrationId = params.get('registration_id');
  // Seed initial state from router state (carries PayNow QR + reference from registration POST,
  // since Portal's GET status endpoint does not return paynow_qr_datauri).
  const seed = location.state || {};
  const [registration, setRegistration] = useState(
    Object.keys(seed).length
      ? {
          paynow_qr_datauri: seed.paynow_qr_datauri,
          payment_reference: seed.payment_reference,
          amount: seed.amount,
          currency: seed.currency,
          payment_instructions: seed.payment_instructions,
          payment_status: 'awaiting_payment',
          payment_method: seed.paynow_qr_datauri ? 'paynow' : null,
          registration_id: registrationId,
        }
      : null
  );
  const [loading, setLoading] = useState(!seed.paynow_qr_datauri);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const pollTimer = useRef(null);

  useEffect(() => {
    if (!registrationId) {
      setError('No registration ID provided.');
      setLoading(false);
      return;
    }

    const fetchStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/events/registrations/${registrationId}`);
        if (!res.ok) {
          if (res.status === 404) throw new Error('Registration not found.');
          throw new Error(`Failed to fetch status (${res.status})`);
        }
        const data = await res.json();
        // Merge polled fields over seed — preserves paynow_qr_datauri from initial POST
        setRegistration((prev) => ({ ...(prev || {}), ...data }));
        setError(null);

        // Stop polling once we reach a terminal state
        if (['paid', 'pending_verification', 'disputed'].includes(data.payment_status) && pollTimer.current) {
          clearInterval(pollTimer.current);
          pollTimer.current = null;
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
    // Poll every 4s while in "awaiting_payment" — cap at 5 minutes (75 attempts) to prevent runaway
    let attempts = 0;
    const maxAttempts = 75;
    pollTimer.current = setInterval(() => {
      attempts += 1;
      if (attempts >= maxAttempts) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
        return;
      }
      fetchStatus();
    }, 4000);
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [registrationId]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  if (error || !registration) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h2 className="font-display text-2xl text-foreground mb-2">Something went wrong</h2>
        <p className="text-muted-foreground mb-6">{error || 'Could not load your registration.'}</p>
        <Button onClick={() => navigate('/register')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Registration Hub
        </Button>
      </div>
    );
  }

  const { payment_status, payment_method, payment_reference, amount, currency, event, child_name, parent_name, membership_number } = registration;

  return (
    <div className="min-h-screen bg-background" data-testid="event-success-page">
      <div className="max-w-2xl mx-auto px-6 py-12 space-y-6">
        <StatusHeader status={payment_status} method={payment_method} />

        {/* Booking summary */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-card gold-border">
            <CardHeader>
              <CardTitle className="font-display text-xl text-primary">Booking Summary</CardTitle>
              <CardDescription>{event?.name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="Child" value={child_name} />
              <Row label="Parent" value={parent_name} />
              <Row label="Amount" value={`$${amount} ${currency}`} />
              <Row label="Method" value={payment_method === 'stripe' ? 'Credit/Debit Card' : 'PayNow'} />
              <Row label="Reference" value={payment_reference} mono copyable onCopy={() => handleCopy(payment_reference)} copied={copied} />
              {membership_number && (
                <Row label="Membership No." value={membership_number} mono />
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* PayNow-specific UI */}
        {payment_method === 'paynow' && payment_status === 'awaiting_payment' && (
          <PayNowInstructions registration={registration} />
        )}

        <div className="text-center space-y-3">
          <Button onClick={() => navigate('/')} variant="outline" data-testid="event-success-home-btn">
            Return to Homepage
          </Button>
          <p className="text-xs text-muted-foreground">
            Questions? Email{' '}
            <a href="mailto:academy@sccfootball.com" className="text-primary hover:underline">
              academy@sccfootball.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

function StatusHeader({ status, method }) {
  const config = {
    paid: {
      icon: <CheckCircle className="h-12 w-12 text-green-400" />,
      title: 'Payment Confirmed!',
      desc: 'Your registration is complete. A confirmation email is on its way.',
      color: 'text-green-300',
    },
    pending_verification: {
      icon: <Clock className="h-12 w-12 text-orange-400" />,
      title: 'Payment Received — Verifying',
      desc: 'We\'ve received your payment notification. Our team will verify and confirm by email within 1 business day.',
      color: 'text-orange-300',
    },
    awaiting_payment: {
      icon: method === 'paynow'
        ? <QrCode className="h-12 w-12 text-yellow-400" />
        : <Loader2 className="h-12 w-12 text-yellow-400 animate-spin" />,
      title: method === 'paynow' ? 'Complete Your PayNow Payment' : 'Awaiting Payment',
      desc: method === 'paynow'
        ? 'Scan the QR code below using your bank app to complete payment.'
        : 'We\'re waiting for payment confirmation from Stripe...',
      color: 'text-yellow-300',
    },
    disputed: {
      icon: <AlertCircle className="h-12 w-12 text-destructive" />,
      title: 'Payment Disputed',
      desc: 'There\'s an issue with this payment. Please contact academy@sccfootball.com.',
      color: 'text-destructive',
    },
  };
  const ui = config[status] || config.awaiting_payment;

  return (
    <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center">
      <div className="flex justify-center mb-4">{ui.icon}</div>
      <h1 className={`font-display text-3xl mb-2 ${ui.color}`} data-testid="event-success-status">
        {ui.title}
      </h1>
      <p className="text-muted-foreground max-w-md mx-auto">{ui.desc}</p>
    </motion.div>
  );
}

function PayNowInstructions({ registration }) {
  const { paynow_qr_datauri, payment_reference, amount, currency, registration_id, payment_instructions } = registration;
  const portalConfirmUrl = `${PORTAL_PAYNOW_PAGE}?ref=${encodeURIComponent(payment_reference || '')}&amount=${amount || ''}&txn=${encodeURIComponent(registration_id || '')}`;

  // Portal-owned copy when available; minimal fallback otherwise.
  // All instruction wording lives on Portal — AS does not edit it.
  const instr = payment_instructions || {};
  const title = instr.title || 'PayNow Instructions';
  const steps = Array.isArray(instr.steps) && instr.steps.length > 0
    ? instr.steps
    : [
        'Open your bank app (DBS, OCBC, UOB, Wise, etc.) and select Scan & Pay.',
        'Scan the QR code above with your bank app.',
        `Confirm the amount: $${amount} ${currency} and complete the transfer.`,
        'After paying, click the button below to confirm the bank account holder\'s name on the account you paid from.',
      ];
  const warnings = Array.isArray(instr.warnings) ? instr.warnings : [];
  const referenceLabel = instr.reference_label || 'Payment Reference (for our records only — do NOT type this into your bank app)';
  const referenceValue = instr.reference_value || payment_reference;

  return (
    <Card className="bg-gradient-to-br from-yellow-900/30 to-amber-900/20 border-yellow-600/50" data-testid="event-paynow-block">
      <CardHeader>
        <CardTitle className="font-display text-xl text-yellow-300 flex items-center gap-2">
          <QrCode className="h-5 w-5" /> {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {paynow_qr_datauri ? (
          <div className="bg-white p-4 rounded-lg mx-auto" style={{ maxWidth: 280 }}>
            <img src={paynow_qr_datauri} alt="PayNow QR code" className="w-full" data-testid="event-paynow-qr" />
          </div>
        ) : (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3 text-yellow-200 text-sm">
            QR code not available. Please contact academy@sccfootball.com.
          </div>
        )}

        <ol className="text-sm space-y-2 text-card-foreground list-decimal list-inside" data-testid="event-paynow-steps">
          {steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>

        {warnings.map((w, i) => (
          <WarningBox key={i} level={w.level || 'info'} text={w.text} testIdx={i} />
        ))}

        {referenceValue && (
          <div className="bg-charcoal border border-border rounded p-3" data-testid="event-paynow-reference">
            <div className="text-xs text-muted-foreground mb-1">{referenceLabel}</div>
            <div className="font-mono text-yellow-300 text-sm break-all">{referenceValue}</div>
          </div>
        )}

        <Button
          asChild
          className="w-full bg-yellow-500 text-black hover:bg-yellow-400 font-bold"
          data-testid="event-paynow-confirm-btn"
        >
          <a href={portalConfirmUrl} target="_blank" rel="noopener noreferrer">
            I've Paid — Confirm Bank Holder Name
            <ExternalLink className="h-4 w-4 ml-2" />
          </a>
        </Button>
      </CardContent>
    </Card>
  );
}

function WarningBox({ level, text, testIdx }) {
  if (!text) return null;
  const styles = {
    info: 'bg-blue-500/10 border-blue-500/40 text-blue-200',
    warning: 'bg-orange-500/10 border-orange-500/40 text-orange-200',
    error: 'bg-red-500/10 border-red-500/40 text-red-200',
  };
  const icons = {
    info: <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />,
    warning: <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />,
    error: <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />,
  };
  return (
    <div
      className={`border rounded p-3 text-sm flex items-start gap-2 ${styles[level] || styles.info}`}
      data-testid={`event-paynow-warning-${testIdx}`}
    >
      {icons[level] || icons.info}
      <span>{text}</span>
    </div>
  );
}

function Row({ label, value, mono = false, copyable = false, onCopy, copied = false }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className={`flex items-center gap-2 ${mono ? 'font-mono text-xs' : ''} text-card-foreground`}>
        {value || '—'}
        {copyable && value && (
          <button type="button" onClick={onCopy} className="text-primary hover:text-primary/80" aria-label="copy">
            {copied ? <CheckCircle className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3" />}
          </button>
        )}
      </span>
    </div>
  );
}
